import { useQuery } from '@tanstack/react-query'
import { History } from 'lucide-react'
import { type KeyboardEvent, useEffect, useState } from 'react'
import { api } from '../../lib/api'
import {
  applySuggestion,
  CLOSED_SUGGESTIONS,
  filterExistingSuggestions,
  formatLastUsed,
  highlightSegments,
  lastAddressSegment,
  SUGGESTION_LIMIT,
  type SuggestionListState,
  shouldSuggest,
  suggestionInitials,
  suggestionKey,
} from '../../lib/mail-address-suggestions'
import { useDebouncedValue } from '../../lib/use-debounced-value'
import { cn } from '../../lib/utils'

/** ARIA 1.2 combobox wiring for the An/Cc input (plain <input> or <Input>). */
export type AddressComboboxProps = {
  role: 'combobox'
  autoComplete: 'off'
  'aria-autocomplete': 'list'
  'aria-expanded': boolean
  'aria-controls': string
  'aria-activedescendant': string | undefined
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void
  onFocus: () => void
  onBlur: () => void
}

type SuggestAccount = { id: string; protected: boolean } | undefined

/**
 * Suggestions from the history of the selected sender account only (#777/#820).
 * Protected or unknown accounts never query (`shouldSuggest`) and render exactly
 * like "no hits" (#524). Data lives only in the react-query memory cache for a
 * short time; the search text never goes to logs.
 */
export function useAddressSuggestions({
  fieldId,
  account,
  value,
  onChange,
}: {
  fieldId: string
  account: SuggestAccount
  value: string
  onChange: (value: string) => void
}) {
  const [focused, setFocused] = useState(false)
  const [list, setList] = useState<SuggestionListState>(CLOSED_SUGGESTIONS)
  const accountId = account?.id ?? ''
  const query = lastAddressSegment(value).query
  const debounced = useDebouncedValue(query)

  // A sender switch closes the list at once; the query key below drops the
  // old account's hits and react-query aborts its request.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on account change only
  useEffect(() => setList(CLOSED_SUGGESTIONS), [accountId])

  const enabled = focused && list.open && shouldSuggest(account, debounced)
  const { data } = useQuery({
    queryKey: ['mail-address-suggestions', accountId, debounced],
    queryFn: ({ signal }) =>
      api.suggestMailAddresses(accountId, debounced, SUGGESTION_LIMIT, signal),
    enabled,
    retry: false,
    staleTime: 30_000,
    gcTime: 30_000,
  })
  // Only hits for exactly the text in the field: nothing stale while the
  // debounce is running.
  const suggestions =
    enabled && debounced === query && data ? filterExistingSuggestions(data, value) : []
  const visible = list.open && suggestions.length > 0
  const active = Math.min(list.active, Math.max(suggestions.length - 1, 0))
  const listId = `${fieldId}-suggestions`
  const optionId = (i: number) => `${fieldId}-suggestion-${i}`

  const pick = (i: number) => {
    const s = suggestions[i]
    if (!s) return
    onChange(applySuggestion(value, s))
    setList(CLOSED_SUGGESTIONS)
  }

  const inputProps: AddressComboboxProps = {
    role: 'combobox',
    autoComplete: 'off',
    'aria-autocomplete': 'list',
    'aria-expanded': visible,
    'aria-controls': listId,
    'aria-activedescendant': visible ? optionId(active) : undefined,
    onKeyDown: (e) => {
      const r = suggestionKey({ open: visible, active }, e.key, visible ? suggestions.length : 0)
      if (!r.handled) {
        // Tab closes without taking over; everything else keeps its old meaning.
        if (r.state.open !== visible) setList(r.state)
        return
      }
      // Esc must close only the list, not the SidePanel listening on window.
      e.preventDefault()
      e.stopPropagation()
      if (r.pick !== undefined) pick(r.pick)
      else setList(r.state)
    },
    onFocus: () => setFocused(true),
    onBlur: () => {
      setFocused(false)
      setList(CLOSED_SUGGESTIONS)
    },
  }

  return {
    change: (next: string) => {
      onChange(next)
      // Only typing in the focused field opens the list (not the pill X).
      if (focused) setList({ open: true, active: 0 })
    },
    inputProps,
    listProps: {
      listId,
      optionId,
      suggestions: visible ? suggestions : [],
      active,
      query,
      onPick: pick,
      onHover: (i: number) => setList({ open: true, active: i }),
    },
  }
}

type ListProps = ReturnType<typeof useAddressSuggestions>['listProps'] & { className?: string }

function Highlighted({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightSegments(text, query).map((s, i) =>
        s.match ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: static segments of one string
          <span key={i} className="text-accent">
            {s.text}
          </span>
        ) : (
          s.text
        ),
      )}
    </>
  )
}

export function AddressSuggestionList({
  listId,
  optionId,
  suggestions,
  active,
  query,
  onPick,
  onHover,
  className,
}: ListProps) {
  const now = new Date()
  return (
    <>
      <div aria-live="polite" className="sr-only">
        {suggestions.length > 0 ? `${suggestions.length} Vorschläge` : ''}
      </div>
      {suggestions.length > 0 && (
        <div
          className={cn(
            'mt-0.5 mb-1.5 overflow-hidden rounded-xl border border-border-default bg-bg-overlay shadow-[0_16px_32px_-12px_rgb(0_0_0/0.7)]',
            className,
          )}
        >
          <div id={listId} role="listbox" aria-label="Adressvorschläge">
            {suggestions.map((s, i) => {
              const on = i === active
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: keys are handled by the combobox input (aria-activedescendant)
                // biome-ignore lint/a11y/useFocusableInteractive: options stay unfocusable so a tap never blurs the input
                <div
                  key={s.address}
                  id={optionId(i)}
                  role="option"
                  aria-selected={on}
                  // Keep focus in the input: a blur would unmount the pills'
                  // input before the click arrives.
                  onPointerDown={(e) => e.preventDefault()}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onPick(i)}
                  onMouseMove={() => !on && onHover(i)}
                  className={cn(
                    'flex min-h-11 cursor-pointer items-center gap-2.5 border-b border-border-subtle px-3 py-2',
                    on && 'bg-accent/10',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                      on ? 'bg-accent/20 text-accent' : 'bg-bg-elevated text-text-secondary',
                    )}
                  >
                    {suggestionInitials(s)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-text-primary">
                      <Highlighted text={s.name?.trim() || s.address} query={query} />
                    </span>
                    <span className="block truncate text-xs text-text-tertiary">
                      {s.name?.trim() ? (
                        <Highlighted text={s.address} query={query} />
                      ) : (
                        'nur Adresse bekannt'
                      )}
                    </span>
                  </span>
                  <span className="shrink-0 text-2xs text-text-tertiary">
                    {formatLastUsed(s.lastUsedAt, now)}
                  </span>
                </div>
              )
            })}
          </div>
          <div
            aria-hidden="true"
            className="flex items-center gap-1.5 px-3 py-[7px] text-2xs text-text-tertiary"
          >
            <History size={11} className="shrink-0" />
            Aus deinem Verlauf in diesem Konto · Enter oder Tippen übernimmt
          </div>
        </div>
      )}
    </>
  )
}
